import { type AccessToken, OktaAuth } from '@okta/okta-auth-js';
import { getIdleDurationMs } from './activityTracker';
import runtimeConfig from './runtimeConfig';

let _oktaAuth: OktaAuth | null = null;

/**
 * Get or initialize OktaAuth instance
 * Lazy initialization ensures runtime config is loaded
 */
const getOktaAuthInstance = (): OktaAuth | null => {
	if (_oktaAuth !== null) {
		return _oktaAuth;
	}

	// Check if Okta is configured
	const isOktaConfigured = !!(
		runtimeConfig.okta.issuer && runtimeConfig.okta.clientId
	);

	if (!isOktaConfigured) {
		return null;
	}

	const oktaConfig = {
		issuer:
			runtimeConfig.okta.issuer ||
			'https://placeholder.okta.com/oauth2/default',
		clientId: runtimeConfig.okta.clientId || 'placeholder-client-id',
		redirectUri:
			runtimeConfig.okta.redirectUri ||
			`${window.location.origin}/login/callback`,
		scopes: [...runtimeConfig.okta.scopes.split(' '), 'offline_access'],
		pkce: true,
		// devMode: runtimeConfig.isDev,
		devMode: false,
	};

	_oktaAuth = new OktaAuth(oktaConfig);
	return _oktaAuth;
};

/**
 * Check if Okta is properly configured
 */
export const isOktaEnabled = () => {
	return getOktaAuthInstance() !== null;
};

/**
 * Initiate Okta login flow
 */
export const loginWithOkta = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		throw new Error(
			'Okta is not configured. Please set OKTA_ISSUER and OKTA_CLIENT_ID environment variables.',
		);
	}
	try {
		await oktaAuth.signInWithRedirect({
			originalUri: '/',
		});
	} catch (error) {
		console.error('Okta login error:', error);
		throw error;
	}
};

/**
 * Handle the callback after Okta authentication
 */
export const handleOktaCallback = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		throw new Error('Okta is not configured');
	}
	try {
		const { tokens } = await oktaAuth.token.parseFromUrl();
		oktaAuth.tokenManager.setTokens(tokens);
		return tokens;
	} catch (error) {
		console.error('Error handling Okta callback:', error);
		throw error;
	}
};

/**
 * Get user information from Okta.
 * Reads claims from the stored ID token first (no network request).
 * Falls back to the /userinfo endpoint if the ID token is unavailable.
 */
export const getOktaUser = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		throw new Error('Okta is not configured');
	}
	try {
		// Prefer reading claims from the stored ID token – this is local
		// (no HTTP request) and avoids network-related failures that can
		// occur in containerised environments.
		const idToken = await oktaAuth.tokenManager.get('idToken');
		if (idToken && 'claims' in idToken && idToken.claims) {
			return idToken.claims;
		}

		// Fallback: fetch from the /userinfo endpoint
		const user = await oktaAuth.getUser();
		return user;
	} catch (error) {
		console.error('Error getting Okta user:', error);
		throw error;
	}
};

/**
 * Logout from Okta
 */
export const logoutFromOkta = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		return; // Silently return if Okta is not configured
	}
	try {
		// Clear tokens from token manager
		await oktaAuth.tokenManager.clear();
		// Then sign out
		await oktaAuth.signOut();
	} catch (error) {
		console.error('Error logging out from Okta:', error);
		throw error;
	}
};

/**
 * Check if user is authenticated with Okta
 */
export const isOktaAuthenticated = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		return false;
	}
	try {
		return await oktaAuth.isAuthenticated();
	} catch (error) {
		console.error('Error checking Okta authentication:', error);
		return false;
	}
};

export const getOktaToken = async () => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) {
		return false;
	}
	try {
		return await oktaAuth.getAccessToken();
	} catch (error) {
		console.error('Error checking Okta authentication:', error);
		return false;
	}
};

export const getValidOktaAccessToken = async (): Promise<string | null> => {
	const oktaAuth = getOktaAuthInstance();
	if (!oktaAuth) return null;

	const token = (await oktaAuth.tokenManager.get('accessToken')) as
		| AccessToken
		| undefined;

	if (!token) return null;

	const now = Math.floor(Date.now() / 1000);
	// console.log('Now:');
	// console.log('TOKEN:', token.expiresAt);
	const expiresIn = token.expiresAt - now;
	// console.log('ExpiresIn:', expiresIn);

	if (expiresIn <= 0) {
		oktaAuth.tokenManager.remove('accessToken');
		window.dispatchEvent(new CustomEvent('okta-token-expired'));
		return null;
	}

	// console.log('Expires in: ' + expiresIn);

	if (expiresIn <= 1500) {
		const IDLE_THRESHOLD_MS = 1_600_000;
		console.log('Idle time:' + IDLE_THRESHOLD_MS);

		if (getIdleDurationMs() < IDLE_THRESHOLD_MS) {
			try {
				const renewed = (await oktaAuth.tokenManager.renew('accessToken')) as
					| AccessToken
					| undefined;
				window.dispatchEvent(new CustomEvent('okta-token-renewed'));
				console.log('Token renewed:', renewed);
				return renewed?.accessToken ?? null;
			} catch (error) {
				console.error('Refresh token renewal failed:', error);
				// Refresh token is gone — force interactive login
				window.dispatchEvent(new CustomEvent('okta-token-expired'));
				return null;
			}
		}
		return token.accessToken;
	}

	return token.accessToken;
};

/**
 * Get or create the OktaAuth instance (lazy — safe to call at any time).
 * Unlike the legacy `oktaAuth` constant, this always re-checks the runtime
 * config, so it works even when the module is evaluated before the config is
 * available.
 */
export const getOktaAuth = (): OktaAuth | null => getOktaAuthInstance();

// Export for backward compatibility (will be null if not configured)
export const oktaAuth = getOktaAuthInstance();

export default oktaAuth;
